import { merge } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { bigQuery } from '@fiverr-private/obs';
import { normalizeOrigin } from '../../helpers/notableClients';
import { BQ_EVENTS, BI_CONSTS } from '../consts';
import { flattenNotableClients } from '../utils';

export const baseBQEventPayload = () => {
    const { userId, pageCtxId, userGuid } = getContext();
    const { CLIENT_ENTITY, DOMAIN, SUB_DOMAIN } = BI_CONSTS;

    return {
        user: {
            id: userId
        },
        uid: userGuid,
        entity: CLIENT_ENTITY,
        publisher: {
            domain: DOMAIN,
            sub_domain: SUB_DOMAIN
        },
        page: {
            ctx_id: pageCtxId
        }
    };
};

const notableClientsHiddenHandler = ({ origin, metadata } = {}) => {
    const { GROUPS: { ACTIONS }, SUB_GROUPS: { NOTABLE_CLIENTS } } = BI_CONSTS;

    return {
        group: ACTIONS,
        sub_group: NOTABLE_CLIENTS,
        page: {
            name: normalizeOrigin(origin),
            component: {
                metadata
            }
        }
    };
};

const notableClientsInteractionHandler = ({
    group,
    subGroup,
    client = {},
    position,
    component,
    origin,
    allClients = [],
    gigId,
    sellerName
} = {}) => ({
    gig: {
        id: gigId
    },
    group,
    sub_group: subGroup,
    page: {
        name: origin,
        element: {
            position
        },
        component: {
            metadata: flattenNotableClients(allClients),
            num_of_elements: allClients.length,
            position: component
        }
    },
    seller: {
        username: sellerName,
        client: {
            name: client.name,
            website: client.website
        }
    }
});

const notableClientsActionInteractionHandler = (params = {}) => {
    const { GROUPS: { ACTIONS }, SUB_GROUPS: { NOTABLE_CLIENTS } } = BI_CONSTS;

    return notableClientsInteractionHandler({ ...params, group: ACTIONS, subGroup: NOTABLE_CLIENTS });
};

const notableClientsImpressionInteractionHandler = (params = {}) => {
    const { GROUPS: { IMPRESSIONS }, SUB_GROUPS: { NOTABLE_CLIENTS } } = BI_CONSTS;

    return notableClientsInteractionHandler({ ...params, group: IMPRESSIONS, subGroup: NOTABLE_CLIENTS });
};

const notableClientsSellerClickedHandler = ({ origin } = {}) => {
    const { userId } = getContext();
    const { GROUPS: { ACTIONS }, SUB_GROUPS: { NOTABLE_CLIENTS }, CLICK_ACTION_TYPE } = BI_CONSTS;

    return {
        seller: {
            id: userId
        },
        group: ACTIONS,
        sub_group: NOTABLE_CLIENTS,
        page: {
            name: origin
        },
        action: {
            type: CLICK_ACTION_TYPE
        }
    };
};

const risingTalentBadgeHoveredHandler = ({ origin, sellerName, gigId } = {}) => {
    const { userId, pageCtxId } = getContext();
    const { GROUPS: { ACTIONS }, SUB_GROUPS: { RISING_TALENT }, CLIENT_ENTITY } = BI_CONSTS;

    return {
        user: {
            id: userId
        },
        entity: CLIENT_ENTITY,
        group: ACTIONS,
        sub_group: RISING_TALENT,
        page: {
            name: origin,
            ctx_id: pageCtxId
        },
        seller: {
            username: sellerName
        },
        gig: {
            id: gigId
        }
    };
};

const verifiedBadgeHoveredHandler = ({ origin, sellerId, position } = {}) => {
    const { pageCtxId } = getContext();
    const { GROUPS: { ACTIONS }, SUB_GROUPS: { VERIFIED_BADGE }, CLIENT_ENTITY } = BI_CONSTS;

    return {
        entity: CLIENT_ENTITY,
        group: ACTIONS,
        sub_group: VERIFIED_BADGE,
        page: {
            name: origin,
            ctx_id: pageCtxId,
            component: {
                position
            }
        },
        seller: {
            id: sellerId
        }
    };
};

const verifiedBadgeEducationPopUpShowHandler = ({ origin, sellerId }) => {
    const { GROUPS: { IMPRESSIONS }, SUB_GROUPS: { VERIFIED_BADGE } } = BI_CONSTS;

    return {
        group: IMPRESSIONS,
        sub_group: VERIFIED_BADGE,
        page: {
            name: origin
        },
        seller: {
            id: sellerId
        }
    };
};

const verifiedClickedGotItEducationHandler = ({ origin, sellerId }) => {
    const { GROUPS: { ACTIONS }, SUB_GROUPS: { VERIFIED_BADGE }, CLICK_ACTION_TYPE } = BI_CONSTS;

    return {
        group: ACTIONS,
        sub_group: VERIFIED_BADGE,
        page: {
            name: origin
        },
        action: {
            type: CLICK_ACTION_TYPE
        },
        seller: {
            id: sellerId
        }
    };
};

const verifiedSignIsShownHandler = ({ origin, sellerId }) => {
    const { GROUPS: { IMPRESSIONS }, SUB_GROUPS: { VERIFIED_BADGE } } = BI_CONSTS;

    return {
        group: IMPRESSIONS,
        sub_group: VERIFIED_BADGE,
        page: {
            name: origin
        },
        seller: {
            id: sellerId
        }
    };
};

const agencyClickHandler = ({ featureName }) => {
    const { userId } = getContext();

    return {
        group: 'user_actions',
        feature: {
            name: featureName
        },
        domain: {
            name: 'agencies'
        },
        page: {
            name: 'seller_page',
            element: {
                type: 'button'
            }
        },
        seller: {
            id: userId
        },
        action: {
            type: 'click'
        }
    };
};

const agencyImpressionHandler = ({ featureName }) => {
    const { userId } = getContext();

    return {
        group: 'user_impressions',
        feature: {
            name: featureName
        },
        domain: {
            name: 'agencies'
        },
        page: {
            name: 'seller_page'
        },
        seller: {
            id: userId
        },
        action: {
            type: 'imp'
        }
    };
};

const sellerHourlyRateImpressionHandler = ({ hourlyRateId }) => {
    const { userId } = getContext();

    return {
        group: 'user_impressions',
        feature: {
            name: 'hourly_rate_setup'
        },
        domain: {
            name: 'hourly'
        },
        page: {
            name: 'seller_page',
            component: {
                metadata: hourlyRateId
            }
        },
        seller: {
            id: userId
        },
        action: {
            type: 'imp'
        }
    };
};

const sellerHourlyRateClickHandler = ({ hourlyRateId }) => {
    const { userId } = getContext();

    return {
        group: 'user_actions',
        feature: {
            name: 'hourly_rate_setup'
        },
        domain: {
            name: 'hourly'
        },
        page: {
            name: 'seller_page',
            element: {
                type: 'button'
            },
            component: {
                metadata: hourlyRateId
            }
        },
        seller: {
            id: userId
        },
        action: {
            type: 'click'
        }
    };
};

const eventParamsGetters = {
    [BQ_EVENTS.HIDDEN]: notableClientsHiddenHandler,
    [BQ_EVENTS.HOVERED_ON_CLIENT]: notableClientsActionInteractionHandler,
    [BQ_EVENTS.CLICKED_ON_CLIENT]: notableClientsActionInteractionHandler,
    [BQ_EVENTS.SHOWN_NOTABLE_CLIENTS_ABOVE_FOLD]: notableClientsImpressionInteractionHandler,
    [BQ_EVENTS.SELLER_CLICKED_ON_ADD_CLIENT]: notableClientsSellerClickedHandler,
    [BQ_EVENTS.HOVERED_ON_RISING_BADGE]: risingTalentBadgeHoveredHandler,
    [BQ_EVENTS.VERIFIED_HOVERED_ON_BADGE]: verifiedBadgeHoveredHandler,
    [BQ_EVENTS.VERIFIED_EDUCATION_POP_UP_SHOWN]: verifiedBadgeEducationPopUpShowHandler,
    [BQ_EVENTS.VERIFIED_CLICKED_ON_GOT_IT_EDUCATIONAL]: verifiedClickedGotItEducationHandler,
    [BQ_EVENTS.VERIFIED_ABOVE_SIGN_IS_SHOWN]: verifiedSignIsShownHandler,
    [BQ_EVENTS.VERIFIED_BELOW_SIGN_IS_SHOWN]: verifiedSignIsShownHandler,
    [BQ_EVENTS.CLICKED_ON_CREATE_AGENCY]: agencyClickHandler,
    [BQ_EVENTS.CLICKED_ON_MANAGE_AGENCY]: agencyClickHandler,
    [BQ_EVENTS.CLICKED_ON_EDIT_AGENCY]: agencyClickHandler,
    [BQ_EVENTS.AGENCY_CREATE_OR_MANAGE_BUTTON_IS_SHOWN]: agencyImpressionHandler,
    [BQ_EVENTS.MANAGE_VIDEO_MEETING_IS_SHOWN]: agencyImpressionHandler,
    [BQ_EVENTS.CLICKED_ON_MANAGE_VIDEO_MEETING]: agencyClickHandler,
    [BQ_EVENTS.SELLER_HOURLY_RATE_SETUP_IS_SHOWN]: sellerHourlyRateImpressionHandler,
    [BQ_EVENTS.CLICKED_ON_SELLER_HOURLY_RATE]: sellerHourlyRateClickHandler
};

const generateReportFunc = (type) => (params = {}) => {
    const getParams = eventParamsGetters[type];

    bigQuery.send(
        merge(
            {},
            baseBQEventPayload(),
            { type },
            getParams(params)
        )
    );
};

export const reportAgencyCreateClicked = generateReportFunc(BQ_EVENTS.CLICKED_ON_CREATE_AGENCY);

export const reportAgencyManageClicked = generateReportFunc(BQ_EVENTS.CLICKED_ON_MANAGE_AGENCY);

export const reportAgencyEditClicked = generateReportFunc(BQ_EVENTS.CLICKED_ON_EDIT_AGENCY);

export const reportAgencyButtonIsShown = generateReportFunc(BQ_EVENTS.AGENCY_CREATE_OR_MANAGE_BUTTON_IS_SHOWN);

export const reportManageVideoMeetingIsShown = generateReportFunc(BQ_EVENTS.MANAGE_VIDEO_MEETING_IS_SHOWN);

export const reportManageVideoMeetingClicked = generateReportFunc(BQ_EVENTS.CLICKED_ON_MANAGE_VIDEO_MEETING);

export const reportNotableClientsHidden = generateReportFunc(BQ_EVENTS.HIDDEN);

export const reportNotableClientsHovered = generateReportFunc(BQ_EVENTS.HOVERED_ON_CLIENT);

export const reportNotableClientsClicked = generateReportFunc(BQ_EVENTS.CLICKED_ON_CLIENT);

export const reportNotableClientsShown = generateReportFunc(BQ_EVENTS.SHOWN_NOTABLE_CLIENTS_ABOVE_FOLD);

export const reportSellerAddClientClicked = generateReportFunc(BQ_EVENTS.SELLER_CLICKED_ON_ADD_CLIENT);

export const reportRisingTalentBadgeHovered = generateReportFunc(BQ_EVENTS.HOVERED_ON_RISING_BADGE);

export const reportVerifiedHoveredOnBadge = generateReportFunc(BQ_EVENTS.VERIFIED_HOVERED_ON_BADGE);

export const reportVerifiedEducationPopUpShown = generateReportFunc(BQ_EVENTS.VERIFIED_EDUCATION_POP_UP_SHOWN);

export const reportVerifiedClickedOnGotItEducational = generateReportFunc(BQ_EVENTS.VERIFIED_CLICKED_ON_GOT_IT_EDUCATIONAL);

export const reportVerifiedAboveSignIsShown = generateReportFunc(BQ_EVENTS.VERIFIED_ABOVE_SIGN_IS_SHOWN);

export const reportVerifiedBelowSignIsShown = generateReportFunc(BQ_EVENTS.VERIFIED_BELOW_SIGN_IS_SHOWN);

export const reportSellerRateSetupIsShown = generateReportFunc(BQ_EVENTS.SELLER_HOURLY_RATE_SETUP_IS_SHOWN);

export const reportSellerRateSetupIsClicked = generateReportFunc(BQ_EVENTS.CLICKED_ON_SELLER_HOURLY_RATE);
